import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import KpiTargetDto from '@/models/KpiTargets/KpiTargetDto';
import CreateKpiTargetRequest from '@/models/KpiTargets/CreateKpiTargetRequest';
import UpdateKpiTargetRequest from '@/models/KpiTargets/UpdateKpiTargetRequest';
import KpiActualsResponse from '@/models/KpiTargets/KpiActualsResponse';
import KpiDashboardItemDto from '@/models/KpiTargets/KpiDashboardItemDto';

export interface IKpiTargetsState {
  loading: boolean;
  kpiTargets: KpiTargetDto[];
  kpiActuals: KpiActualsResponse | null;
  kpiDashboardItems: KpiDashboardItemDto[];
}

@Module({ dynamic: true, store, name: 'KpiTargets', namespaced: true })
class KpiTargets extends VuexModule implements IKpiTargetsState {

  public loading = false;
  public kpiTargets: KpiTargetDto[] = [];
  public kpiActuals: KpiActualsResponse | null = null;
  public kpiDashboardItems: KpiDashboardItemDto[] = [];

  @Action
  public async loadKpiTargets(params: { branchId?: string; deskId?: string; agencyUserId?: string; kpiCode?: string; entityType?: number; activeOnly?: boolean }) {
    this.setLoading(true);
    const queryParams = new URLSearchParams();
    if (params.branchId) queryParams.append('branchId', params.branchId);
    if (params.deskId) queryParams.append('deskId', params.deskId);
    if (params.agencyUserId) queryParams.append('agencyUserId', params.agencyUserId);
    if (params.kpiCode) queryParams.append('kpiCode', params.kpiCode);
    if (params.entityType !== undefined && params.entityType !== null) queryParams.append('entityType', String(params.entityType));
    if (params.activeOnly) queryParams.append('activeOnly', 'true');

    const qs = queryParams.toString();
    const url = `/api/v1/agency/kpi-targets${qs ? '?' + qs : ''}`;

    await ajax.get(url)
      .then(response => {
        this.context.commit('setKpiTargets', response.data || []);
        this.setLoading(false);
      })
      .catch(error => {
        AppModule.handleApiError(error);
        this.setLoading(false);
      });
  }

  @Action
  public async createKpiTarget(request: CreateKpiTargetRequest): Promise<KpiTargetDto | null> {
    this.setLoading(true);
    try {
      const response = await ajax.post('/api/v1/agency/kpi-targets', request);
      this.setLoading(false);
      return response.data;
    } catch (error) {
      AppModule.handleApiError(error);
      this.setLoading(false);
      return null;
    }
  }

  @Action
  public async updateKpiTarget(payload: { id: string; request: UpdateKpiTargetRequest }): Promise<KpiTargetDto | null> {
    this.setLoading(true);
    try {
      const response = await ajax.put(`/api/v1/agency/kpi-targets/${payload.id}`, payload.request);
      this.setLoading(false);
      return response.data;
    } catch (error) {
      AppModule.handleApiError(error);
      this.setLoading(false);
      return null;
    }
  }

  @Action
  public async deleteKpiTarget(id: string): Promise<boolean> {
    this.setLoading(true);
    try {
      await ajax.delete(`/api/v1/agency/kpi-targets/${id}`);
      this.setLoading(false);
      return true;
    } catch (error) {
      AppModule.handleApiError(error);
      this.setLoading(false);
      return false;
    }
  }

  @Action
  public async loadKpiActuals(params: { kpiCode: string; periodFrom: string; periodTo: string; branchId?: string; deskId?: string; agencyUserId?: string }) {
    this.setLoading(true);
    const queryParams = new URLSearchParams();
    queryParams.append('kpiCode', params.kpiCode);
    queryParams.append('periodFrom', params.periodFrom);
    queryParams.append('periodTo', params.periodTo);
    if (params.branchId) queryParams.append('branchId', params.branchId);
    if (params.deskId) queryParams.append('deskId', params.deskId);
    if (params.agencyUserId) queryParams.append('agencyUserId', params.agencyUserId);

    await ajax.get(`/api/v1/agency/kpi-targets/actuals?${queryParams.toString()}`)
      .then(response => {
        this.context.commit('setKpiActuals', response.data);
        this.setLoading(false);
      })
      .catch(error => {
        AppModule.handleApiError(error);
        this.setLoading(false);
      });
  }

  @Action
  public async bulkCreateKpiTargets(request: any): Promise<{ created: number } | null> {
    this.setLoading(true);
    try {
      const response = await ajax.post('/api/v1/agency/kpi-targets/bulk', request);
      this.setLoading(false);
      return response.data;
    } catch (error) {
      AppModule.handleApiError(error);
      this.setLoading(false);
      return null;
    }
  }

  @Action
  public async loadKpiDashboard(params?: { referenceDate?: string; periodType?: number; reportingPeriodId?: string | null }) {
    this.setLoading(true);
    const queryParams = new URLSearchParams();
    if (params && params.referenceDate) {
      queryParams.append('referenceDate', params.referenceDate);
    }
    if (params && params.periodType) {
      queryParams.append('periodType', String(params.periodType));
    }
    if (params && params.reportingPeriodId) {
      queryParams.append('reportingPeriodId', params.reportingPeriodId);
    }
    const qs = queryParams.toString();
    const url = `/api/v1/agency/kpi-dashboard${qs ? '?' + qs : ''}`;

    await ajax.get(url)
      .then(response => {
        this.context.commit('setKpiDashboardItems', response.data || []);
        this.setLoading(false);
      })
      .catch(error => {
        AppModule.handleApiError(error);
        this.setLoading(false);
      });
  }

  @Action
  cleanState() {
    this.context.commit('resetState');
  }

  @Action
  public clearKpiDashboardItems() {
    this.context.commit('setKpiDashboardItems', []);
  }

  @Mutation
  resetState() {
    this.loading = false;
    this.kpiTargets = [];
    this.kpiActuals = null;
    this.kpiDashboardItems = [];
  }

  @Mutation
  private setLoading(isLoading: boolean) {
    this.loading = isLoading;
  }

  @Mutation
  private setKpiTargets(targets: KpiTargetDto[]) {
    this.kpiTargets = targets;
  }

  @Mutation
  private setKpiActuals(actuals: KpiActualsResponse) {
    this.kpiActuals = actuals;
  }

  @Mutation
  private setKpiDashboardItems(items: KpiDashboardItemDto[]) {
    this.kpiDashboardItems = items;
  }
}

export const KpiTargetsModule = getModule(KpiTargets);
